import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  PlayArrow as PlayIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import WorkflowService from '../services/WorkflowService';
import { useEnvironment } from '../context/EnvironmentContext';
import { useApiToken } from '../hooks/useApiToken';
import WorkflowViewModal from '../components/Modals/WorkflowViewModal';
import ExecuteWorkflowModal from '../components/Modals/ExecuteWorkflowModal';

interface Workflow {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'inactive' | 'error';
  lastRun: string;
  nodes: number;
  createdAt: string;
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { environment } = useEnvironment();
  const getAccessToken = useApiToken();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedViewWorkflow, setSelectedViewWorkflow] = useState<Workflow | null>(null);
  const [fullWorkflowDetails, setFullWorkflowDetails] = useState<any | null>(null);
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [selectedExecuteWorkflow, setSelectedExecuteWorkflow] = useState<any | null>(null);

  useEffect(() => {
    fetchWorkflows();
    // eslint-disable-next-line
  }, [environment]);

  const fetchWorkflows = async () => {
    setLoading(true);
    setError(null);
    try {
      const accessToken = await getAccessToken();
      const workflowService = new WorkflowService(environment, accessToken);
      const response = await workflowService.listWorkflows();
      console.log('Workflows API response:', response);
      let apiWorkflows: any[] = [];
      if (Array.isArray(response?.data?.workflows)) {
        apiWorkflows = response.data.workflows;
      } else {
        apiWorkflows = [];
      }
      const mapped = apiWorkflows.map((w: any) => ({
        id: w.id || w._id || '',
        name: w.name || '',
        description: w.description || '',
        status: w.status || 'active',
        lastRun: w.last_run || w.lastRun || '',
        nodes: w.nodes?.length || w.nodes || 0,
        createdAt: w.created_at || w.createdAt || '',
      }));
      setWorkflows(mapped);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch workflows');
      setWorkflows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkflow = () => {
    navigate('/flow-editor');
  };

  const handleEditWorkflow = (workflow: Workflow) => {
    navigate(`/flow-editor/${workflow.id}`);
  };

  const handleDeleteWorkflow = (workflow: Workflow) => {
    setSelectedWorkflow(workflow);
    setShowDeleteDialog(true);
  };

  const handleViewWorkflow = async (workflow: Workflow) => {
    setShowViewDialog(true);
    try {
      const accessToken = await getAccessToken();
      const workflowService = new WorkflowService(environment, accessToken);
      const response = await workflowService.getWorkflow(workflow.id);
      setFullWorkflowDetails(response.data);
    } catch (err) {
      setFullWorkflowDetails(null);
      enqueueSnackbar('Failed to fetch workflow details', { variant: 'error' });
    }
  };

  const handleExecuteWorkflow = async (workflow: Workflow) => {
    try {
      const accessToken = await getAccessToken();
      const workflowService = new WorkflowService(environment, accessToken);
      const response = await workflowService.getWorkflow(workflow.id);
      setSelectedExecuteWorkflow(response.data);
      setShowExecuteModal(true);
    } catch (err) {
      enqueueSnackbar('Failed to fetch workflow for execution', { variant: 'error' });
    }
  };

  const confirmDelete = async () => {
    if (selectedWorkflow) {
      try {
        const accessToken = await getAccessToken();
        const workflowService = new WorkflowService(environment, accessToken);
        await workflowService.deleteWorkflow(selectedWorkflow.id);
        setWorkflows(prev => prev.filter(w => w.id !== selectedWorkflow.id));
        enqueueSnackbar('Workflow deleted successfully', { variant: 'success' });
      } catch (err: any) {
        enqueueSnackbar(err.message || 'Failed to delete workflow', { variant: 'error' });
      } finally {
        setShowDeleteDialog(false);
        setSelectedWorkflow(null);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const filteredWorkflows = workflows.filter(workflow =>
    workflow.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    workflow.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '60vh', justifyContent: 'center' }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="contained" onClick={fetchWorkflows}>Retry</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
          Workflows
        </Typography>
        <Typography variant="body1" color="text.secondary">
          List and manage your AI workflows
        </Typography>
      </Box>

      {/* Search and Actions */}
      <Card sx={{ mb: 4, borderRadius: 3, background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', border: '1px solid rgba(0,0,0,0.05)' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              placeholder="Search workflows..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 300 }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateWorkflow}
              sx={{
                background: 'linear-gradient(45deg, #6366f1, #4f46e5)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #4f46e5, #3730a3)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 8px 25px rgba(99, 102, 241, 0.3)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Create Workflow
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Workflows Table */}
      <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', border: '1px solid rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Workflow</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Nodes</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Last Run</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredWorkflows.map((workflow, index) => (
                  <TableRow key={`${workflow.id}-${index}`} sx={{ '&:hover': { backgroundColor: 'grey.50' } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                        {workflow.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="600">
                          {workflow.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {workflow.description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={workflow.status}
                        color={getStatusColor(workflow.status) as any}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${workflow.nodes} nodes`}
                        variant="outlined"
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {workflow.lastRun}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {workflow.createdAt}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => handleViewWorkflow(workflow)}
                            sx={{ color: 'info.main' }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Execute">
                          <IconButton
                            size="small"
                            onClick={() => handleExecuteWorkflow(workflow)}
                            sx={{ color: 'success.main' }}
                          >
                            <PlayIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEditWorkflow(workflow)}
                            sx={{ color: 'primary.main' }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteWorkflow(workflow)}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            Delete Workflow
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete "{selectedWorkflow?.name}"? This action cannot be undone.
          </Typography>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            This will permanently remove the workflow and all its associated data.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setShowDeleteDialog(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(45deg, #ef4444, #dc2626)',
              '&:hover': {
                background: 'linear-gradient(45deg, #dc2626, #b91c1c)'
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Workflow Details Dialog */}
      <WorkflowViewModal
        open={showViewDialog}
        onClose={() => setShowViewDialog(false)}
        workflow={fullWorkflowDetails}
      />

      {/* Execute Workflow Modal */}
      <ExecuteWorkflowModal
        open={showExecuteModal}
        onClose={() => setShowExecuteModal(false)}
        workflow={selectedExecuteWorkflow}
      />
    </Box>
  );
};

export default DashboardPage;
